import { PlatformTypes } from "@app/models";
import {
  ExternalFeatureCode,
  PermissionTypeDto,
  PosMarketplaceAvailability,
  PosMarketplaceAvailabilityDto,
  PosMarketConnectedCatalogItemDto,
  PosMarketOpenMode,
  SubscriptionPlanShortGetDto
} from "clearline-api";

export enum ParentMenuIdentifier {
  Sites = "PARENT_SITES"
}

export type MenuItemIdentifier = PermissionTypeDto | ParentMenuIdentifier;

/**
 * Returns a flat array of all `NavMenuItem`s including nested `subItems` recursively.
 * @param menuItems - Array of NavMenuItem objects to flatten
 * @param result - Optional accumulator array (used for recursion)
 * @returns Flattened array containing all menu items including nested ones
 */
export function flattenNavMenuItems(menuItems: NavMenuItem[], result: NavMenuItem[] = []): NavMenuItem[] {
  if (!menuItems?.length) {
    return result;
  }

  for (const menuItem of menuItems) {
    result.push(menuItem);
    if (menuItem.subItems?.length) {
      flattenNavMenuItems(menuItem.subItems, result);
    }
  }

  return result;
}

/**
 * Returns a copy of `menuItems` where every item whose `options.featureCode` has an entry in
 * `urlByFeatureCode` opens that URL instead of its statically configured link. Used to let the API
 * hand a partner admin an auto login URL for a feature; items without a matching code keep the link
 * defined in the menu.
 *
 * @param menuItems - Array of NavMenuItem objects to process
 * @param urlByFeatureCode - Resolved URLs keyed by external feature code
 * @returns New array with matching items pointed at the resolved URLs
 */
export function applyExternalFeatureUrls(menuItems: NavMenuItem[], urlByFeatureCode: Map<string, string>): NavMenuItem[] {
  return menuItems.map((item) => {
    const resolvedUrl = item.options?.featureCode ? urlByFeatureCode.get(item.options.featureCode) : null;

    if (resolvedUrl) {
      return {
        ...item,
        action: SidebarAction.OpenHyperLink,
        options: { ...item.options, hyperLink: resolvedUrl, newTab: true }
      };
    }

    return item.subItems?.length ? { ...item, subItems: applyExternalFeatureUrls(item.subItems, urlByFeatureCode) } : item;
  });
}

/**
 * Updates menu item visibility based on their permissions and children's visibility.
 * For parent items with children, they will be shown if any child is visible,
 * regardless of the parent's own permission.
 *
 * @param menuItems - The menu items to update visibility for
 * @param permissionsMap - A map of feature types to permission values
 * @returns The updated menu items
 */
export function updateMenuItemsVisibility(menuItems: NavMenuItem[], permissionsMap: Record<PermissionTypeDto, boolean>): NavMenuItem[] {
  // Create a deep copy to avoid mutating the original
  const updatedMenuItems = JSON.parse(JSON.stringify(menuItems));

  // Apply visibility and locked state updates
  processMenuItemsVisibility(updatedMenuItems, permissionsMap);

  return updatedMenuItems;
}

/**
 * Internal helper function that processes menu item visibility and locked states recursively.
 *
 * Visibility Rules:
 * - Items without identifier are always visible
 * - Items with identifier are visible if they have permission
 * - Locked items are always visible
 * - Parent items are visible if they have any visible children or are locked
 *
 * Locked State Rules:
 * - Items are locked when identifierLocked is present
 * - Locked state takes priority over permission state
 * - Parent items maintain locked state even with hidden children
 *
 * @param items - Array of menu items to process
 * @param permissionsMap - Map of feature types to their permission states
 */
function processMenuItemsVisibility(items: NavMenuItem[], permissionsMap: Record<PermissionTypeDto, boolean>): void {
  items.forEach((item) => {
    const hasPermission = !!permissionsMap[item.identifier];
    const hasLockedPermission = !!item.identifierLocked;
    let isVisible = false;
    let isLocked = false;

    if (item.identifier) {
      isVisible = hasPermission;
    } else {
      isVisible = true; // allow access if no identifier (super admin)
    }

    if (hasLockedPermission) {
      isVisible = true;
      isLocked = true;
    }

    // Handle cases where both identifier and locked state exist
    if (item.identifier && hasLockedPermission) {
      if (hasLockedPermission) {
        isVisible = true;
        isLocked = true;
      } else if (hasPermission) {
        isVisible = true;
        isLocked = false;
      } else {
        isVisible = false;
        isLocked = false;
      }
    }

    item.hidden = !isVisible;
    item.locked = isLocked;

    if (item.subItems?.length) {
      processMenuItemsVisibility(item.subItems, permissionsMap);
    }
  });

  items.forEach((item) => {
    if (item.subItems?.length) {
      const hasVisibleChild = item.subItems.some((child) => !child.hidden);
      if (hasVisibleChild) {
        item.hidden = false; // visible if any child is visible
      } else if (!item.locked) {
        item.hidden = true; // hide if no visible children and isn't locked
      }
    }
  });
}

/**
 * Recursively processes menu items to mark locked features based on user permissions
 * @param menuItems Array of menu items to process
 * @param lockedFeaturesMap Map of locked features to next min plans
 * @returns New array with locked features marked
 */
export function processLockedFeatures(
  menuItems: NavMenuItem[],
  lockedFeaturesMap: Map<PermissionTypeDto, SubscriptionPlanShortGetDto>
): NavMenuItem[] {
  const isFeatureType = (identifier: unknown): identifier is PermissionTypeDto =>
    // typeof identifier === "string" && identifier in PermissionTypeDto; // fixme: remove after backend updates
    typeof identifier === "string" && Object.values(PermissionTypeDto).includes(identifier as PermissionTypeDto);

  const processMenuItem = (item: NavMenuItem): NavMenuItem => {
    const identifier: PermissionTypeDto = item.identifier as PermissionTypeDto;
    const hasValidIdentifier = identifier && isFeatureType(identifier);
    const isLocked = hasValidIdentifier && lockedFeaturesMap.has(identifier);

    const processedItem: NavMenuItem = {
      ...item,
      ...(isLocked && hasValidIdentifier && { identifierLocked: identifier })
    };

    if (item.subItems?.length) {
      processedItem.subItems = item.subItems.map(processMenuItem);
    }

    return processedItem;
  };

  return menuItems.map(processMenuItem);
}

export function applyPosMarketplaceAvailability(
  menuItems: NavMenuItem[],
  availability: PosMarketplaceAvailabilityDto | null
): NavMenuItem[] {
  return menuItems
    .filter((item) => item.identifier !== PermissionTypeDto.PosMarketplace || isPosMarketplaceOffered(availability))
    .map((item) => {
      if (item.identifier === PermissionTypeDto.PosMarketplace) {
        return availability.status === PosMarketplaceAvailability.Locked
          ? { ...item, identifierLocked: PermissionTypeDto.PosMarketplace, canUpgrade: !!availability.unlock?.canUpgradePlan }
          : { ...item, identifierLocked: undefined };
      }

      return item.subItems?.length ? { ...item, subItems: applyPosMarketplaceAvailability(item.subItems, availability) } : item;
    });
}

function isPosMarketplaceOffered(availability: PosMarketplaceAvailabilityDto | null): boolean {
  return availability?.status === PosMarketplaceAvailability.Available || availability?.status === PosMarketplaceAvailability.Locked;
}

export function applyConnectedPosItem(menuItems: NavMenuItem[], connectedItem: PosMarketConnectedCatalogItemDto | null): NavMenuItem[] {
  const connectedMenuItem = connectedItem ? toConnectedPosMenuItem(connectedItem) : null;

  if (!connectedMenuItem) {
    return menuItems;
  }

  return menuItems.map((item) => {
    if (item.identifier === PermissionTypeDto.PosMarketplace) {
      return { ...item, subItems: [connectedMenuItem] };
    }

    return item.subItems?.length ? { ...item, subItems: applyConnectedPosItem(item.subItems, connectedItem) } : item;
  });
}

function toConnectedPosMenuItem({ name, logoUrl, url, openMode }: PosMarketConnectedCatalogItemDto): NavMenuItem | null {
  const menuItem: NavMenuItem = {
    label: name,
    untranslatedLabel: true,
    iconUrl: logoUrl,
    identifier: PermissionTypeDto.PosMarketplace,
    connected: true,
    hidden: true
  };

  switch (openMode) {
    case PosMarketOpenMode.Iframe:
      return { ...menuItem, routePath: "/manager/pos-marketplace/pos-data-sync" };
    case PosMarketOpenMode.NewWindow:
      return { ...menuItem, action: SidebarAction.OpenHyperLink, options: { hyperLink: url, newTab: true }, external: true };
    default:
      return null;
  }
}

export const SidebarItemOptionsParam = {
  ...PlatformTypes,
  Connectors: "Connectors",
  SitesBuilder: "SitesBuilder"
} as const;

export type SidebarItemOptionsParam = PlatformTypes | "Connectors" | "SitesBuilder";

export enum SidebarAction {
  OpenPlatform = "OpenPlatform",
  OpenExternalFeature = "OpenExternalFeature",
  OpenExternalFeatureByPermission = "OpenExternalFeatureByPermission",
  OpenCmc = "OpenCmc",
  OpenDigitalScreensDashboard = "OpenDigitalScreensDashboard",
  OpenHyperLink = "OpenHyperLink"
}

export interface SidebarItemOptions {
  newTab?: boolean;
  params?: SidebarItemOptionsParam; // todo: rename it. featureCode?
  pathWithParam?: boolean;
  permission?: PermissionTypeDto;
  hyperLink?: string;
  /** External feature this item opens. Lets a resolved auto login URL replace the item's static link. */
  featureCode?: ExternalFeatureCode;
}

export interface NavMenuItem {
  /** Label, that will be translated (Default: src/assets/i18n/en.json). */
  label?: string;

  /** Icon class. `Remix Icon` mostly. */
  icon?: string;

  iconUrl?: string;

  connected?: boolean;

  external?: boolean;

  /** Indicates if the item has an info tooltip */
  hasInfoTooltip?: boolean;

  /** Uniq identifier for custom item handling (by permission or by parent menu identifier). */
  identifier?: MenuItemIdentifier;

  /** Identifier for locked state handling. */
  identifierLocked?: PermissionTypeDto;

  /** This label should remain untranslated */
  untranslatedLabel?: boolean;

  /** Controls visibility (hidden items are not rendered) */
  hidden?: boolean;

  /** Controls locked state (locked items are visible but disabled) */
  locked?: boolean;

  canUpgrade?: boolean;

  /** Route path for navigation */
  routePath?: string;

  /** Action to be performed when the item is selected */
  action?: SidebarAction;

  /** Additional options for the sidebar item */
  options?: SidebarItemOptions;

  /** Nested sub-items for hierarchical menus */
  subItems?: NavMenuItem[];
}
